<template>
  <div
    class="genericResourceFormShell"
    :class="{ 'genericResourceFormShell--awaitType': isAwaitingType }"
  >
    <el-card class="card linkFormSection" data-section-id="basic">
          <div slot="header" class="linkFormSection__header">
            <strong class="linkFormSection__title">基本信息</strong>
            <span
              v-if="showCompletionStatus"
              class="linkFormSection__status"
              :class="completion.sectionStatus.basic === 'done'
                ? 'linkFormSection__status--done'
                : 'linkFormSection__status--pending'"
            >
              {{ completion.sectionStatus.basic === 'done' ? '✓ 已完成' : '未完成' }}
            </span>
          </div>
          <div class="linkFormBasic" :class="{ 'linkFormBasic--zipBand': useZipBandLayout }">
            <div class="linkFormBasic__media">
              <div class="linkFormMediaAnchor">
                <el-image
                  v-if="!isAwaitingType && editPreviewUrl && (isAiAssistantType || isWebCoverType || (requiresFileUpload && !useZipUploadComponent))"
                  class="linkFormMediaAnchor__preview"
                  :src="editPreviewUrl"
                  :preview-src-list="previewSrcList"
                  fit="cover"
                >
                  <div slot="error" class="linkFormMediaAnchor__previewFallback">
                    <i class="el-icon-picture-outline" aria-hidden="true" />
                  </div>
                </el-image>
                <generic-resource-zip-upload
                  v-else-if="!isAwaitingType && requiresFileUpload && useZipUploadComponent"
                  :url.sync="ruleForm.jsonConfig.url"
                  :asset-type="ruleForm.assetType"
                  :disabled="isAwaitingType"
                  @uploaded="onZipUploaded"
                  @cleared="clearUpload"
                />
                <div
                  v-else-if="!isAwaitingType && showUploadDone && !useZipUploadComponent"
                  class="linkFormMediaAnchor__fileTile"
                  :title="ruleForm.jsonConfig.url"
                >
                  <i class="el-icon-document linkFormMediaAnchor__fileIcon" aria-hidden="true" />
                  <span class="linkFormMediaAnchor__fileName">{{ uploadUrlDisplay }}</span>
                  <div
                    v-if="requiresFileUpload && !editPreviewUrl"
                    class="linkFormMediaAnchor__fileActions"
                  >
                    <button type="button" class="linkFormMediaFileBar__btn" @click="uploadUrl">
                      替换
                    </button>
                    <button
                      type="button"
                      class="linkFormMediaFileBar__btn linkFormMediaFileBar__btn--danger"
                      @click="clearUpload"
                    >
                      移除
                    </button>
                  </div>
                </div>
                <div v-else-if="isAiAssistantType" class="linkFormMediaAnchor__noFile">
                  <i class="el-icon-user linkFormMediaAnchor__noFileIcon" aria-hidden="true" />
                  <span>在下方 Ai 角色配置上传 avatar</span>
                </div>
                <!-- web：基本信息左侧恢复封面上传（写 thumbUrl，不占 jsonConfig.url） -->
                <button
                  v-else-if="!isAwaitingType && isWebCoverType"
                  type="button"
                  class="linkFormUploadZone linkFormUploadZone--anchor"
                  data-field-key="thumbUrl"
                  @click="uploadWebCover"
                >
                  <i class="el-icon-upload2 linkFormUploadZone__icon" aria-hidden="true" />
                  <span>上传封面图</span>
                  <span class="linkFormUploadZone__hint">jpg / png / webp / gif</span>
                </button>
                <button
                  v-else-if="isAwaitingType || (requiresFileUpload && !useZipUploadComponent)"
                  type="button"
                  class="linkFormUploadZone linkFormUploadZone--anchor"
                  :class="{ 'linkFormUploadZone--disabled': isAwaitingType }"
                  :disabled="isAwaitingType"
                  @click="uploadUrl"
                >
                  <i class="el-icon-upload2 linkFormUploadZone__icon" aria-hidden="true" />
                  <span>上传资源文件</span>
                  <span class="linkFormUploadZone__hint">{{ uploadZoneHint }}</span>
                </button>
                <div v-else class="linkFormMediaAnchor__noFile">
                  <i class="el-icon-edit-outline linkFormMediaAnchor__noFileIcon" aria-hidden="true" />
                  <span>配置型</span>
                </div>
              </div>

              <div
                v-if="showUploadDone && requiresFileUpload && editPreviewUrl && !useZipUploadComponent"
                class="linkFormMediaFileBar"
              >
                <span class="linkFormMediaFileBar__name" :title="ruleForm.jsonConfig.url">
                  {{ uploadUrlDisplay }}
                </span>
                <div class="linkFormMediaFileBar__actions">
                  <button type="button" class="linkFormMediaFileBar__btn" @click="uploadUrl">
                    替换文件
                  </button>
                  <button
                    type="button"
                    class="linkFormMediaFileBar__btn linkFormMediaFileBar__btn--danger"
                    @click="clearUpload"
                  >
                    移除
                  </button>
                </div>
              </div>

              <div
                v-if="isWebCoverType && editPreviewUrl"
                class="linkFormMediaFileBar"
              >
                <span class="linkFormMediaFileBar__name" :title="ruleForm.thumbUrl">
                  {{ webCoverDisplayName }}
                </span>
                <div class="linkFormMediaFileBar__actions">
                  <button type="button" class="linkFormMediaFileBar__btn" @click="uploadWebCover">
                    替换封面
                  </button>
                  <button
                    type="button"
                    class="linkFormMediaFileBar__btn linkFormMediaFileBar__btn--danger"
                    @click="clearWebCover"
                  >
                    移除
                  </button>
                </div>
              </div>

              <el-form-item
                v-show="requiresFileUpload"
                prop="jsonConfig.url"
                class="linkFormBasic__urlField"
              >
                <el-input v-show="false" v-model="ruleForm.jsonConfig.url" />
              </el-form-item>
              <el-form-item
                v-show="isWebCoverType"
                prop="thumbUrl"
                class="linkFormBasic__urlField"
                data-field-key="thumbUrl"
              >
                <el-input v-show="false" v-model="ruleForm.thumbUrl" />
              </el-form-item>
            </div>

            <div class="linkFormBasic__fields">
              <div class="linkFormBasic__meta">
                <el-form-item prop="resourceName" class="linkFormBasic__nameField" data-field-key="resourceName">
                  <template slot="label">
                    <span class="linkFormBasic__nameLabelRow">
                      <span class="linkFormBasic__nameLabelText">资源名称</span>
                      <span v-if="!isCreate" class="linkFormBasic__keyInline">
                        <span class="linkFormBasic__keyLabel">Key</span>
                        <template v-if="ruleForm.key">
                          <code class="linkFormBasic__keyValue link-font-mono" :title="ruleForm.key">
                            {{ displayKey }}
                          </code>
                          <button type="button" class="linkFormBasic__copyBtn" @click="copyKey">
                            复制
                          </button>
                        </template>
                        <span v-else class="linkFormBasic__keyEmpty">—</span>
                      </span>
                    </span>
                  </template>
                  <el-input
                    v-model="ruleForm.resourceName"
                    :disabled="isAwaitingType"
                    :placeholder="isAwaitingType ? '请先在顶栏选择资源类型' : '请输入资源名称'"
                  />
                </el-form-item>
              </div>

              <div v-if="showZipInDetailSlot" class="linkFormBasic__detailSlot">
                <zip-package-requirement :spec="zipPackageSpec" :asset-type="ruleForm.assetType" />
              </div>
              <ai-assistant-basic-info-summary
                v-else-if="isAiAssistantType && !useZipBandLayout"
                :rule-form="ruleForm"
              />
              <basic-info-idle-anim v-else-if="!useZipBandLayout" :hint="idleAnimHint" />
            </div>

            <div v-if="useZipBandLayout" class="linkFormBasic__zipBand">
              <zip-package-requirement :spec="zipPackageSpec" :asset-type="ruleForm.assetType" />
            </div>
          </div>
        </el-card>

      <el-card
        v-if="showTypeConfigSection"
        ref="typeConfigSection"
        class="card linkFormSection linkFormSection--typeConfig"
        data-section-id="typeConfig"
        :class="{ 'linkFormSection--invalid': typeConfigValidationFailed }"
      >
        <div slot="header" class="linkFormSection__header">
          <strong class="linkFormSection__title">{{ typeConfigSectionTitle }}</strong>
          <span
            v-if="showCompletionStatus"
            class="linkFormSection__status"
            :class="typeConfigStatusClass"
          >
            {{ typeConfigStatusLabel }}
          </span>
        </div>
        <generic-resource-basic-form-host
          ref="basicForm"
          :asset-type="ruleForm.assetType"
          :rule-form="ruleForm"
          :mode="isCreate ? 'create' : 'edit'"
          :show-zip-requirement="false"
          @completion-changed="bumpCompletion"
        />
      </el-card>

      <el-card
        v-if="showSchemaSection"
        ref="schemaConfigSection"
        class="card linkFormSection linkFormSection--schema"
        data-section-id="schema"
        :class="{ 'linkFormSection--invalid': schemaValidationFailed }"
      >
        <div slot="header" class="linkFormSection__header">
          <strong class="linkFormSection__title">{{ schemaSectionTitle }}</strong>
          <span
            v-if="showCompletionStatus"
            class="linkFormSection__status"
            :class="completion.sectionStatus.schema === 'done'
              ? 'linkFormSection__status--done'
              : 'linkFormSection__status--pending'"
          >
            {{ completion.sectionStatus.schema === 'done' ? '✓ 已完成' : '未完成' }}
          </span>
        </div>
        <generic-resource-basic-form-host
          ref="schemaBasicForm"
          :asset-type="ruleForm.assetType"
          :mode="isCreate ? 'create' : 'edit'"
          :show-zip-requirement="false"
          @completion-changed="bumpCompletion"
        />
      </el-card>

      <div
        class="linkFormGovernanceGate"
        :class="{ 'linkFormGovernanceGate--locked': isAwaitingType }"
      >
        <component
          :is="resolveGovernanceComponent(section)"
          v-for="section in layoutSections"
          :key="section.id"
          :ref="'gov-' + section.id"
          v-bind="governanceProps(section.id)"
        />
      </div>
  </div>
</template>

<script>
import { uploadFile } from '../../../../api/comment'
import {
  isNonFileType,
  isZipOnlyType,
  isAiAssistantType,
  isWebCoverType as matchWebCoverType,
  requiresFileUpload,
  validateLocalFileForAssetType,
  validateUploadedUrlForAssetType,
} from '../genericResourceUploadRules'
import { computeGenericResourceCompletion } from './genericResourceFormCompletion'
import {
  resolveFormLayout,
  GENERIC_RESOURCE_BASIC_FORM_MODE,
  resolveGenericResourceTypeEntry,
  getZipPackageSpec,
  shouldShowZipPackageRequirement,
  shouldShowTypeConfigSection,
  getTypeConfigSectionTitle,
  getSchemaSectionTitle,
} from './genericResourceFormRegistry'
import GenericResourceBasicFormHost from './GenericResourceBasicFormHost.vue'
import GenericResourceZipUpload from './GenericResourceZipUpload.vue'
import { stickerNameFromFile } from './stickerResourceName'
import ZipPackageRequirement from './basicForms/ZipPackageRequirement.vue'
import BasicInfoIdleAnim from './basicForms/BasicInfoIdleAnim.vue'
import AiAssistantBasicInfoSummary from './basicForms/AiAssistantBasicInfoSummary.vue'
import GENERIC_RESOURCE_GOVERNANCE_COMPONENTS from '../../../../components/resourceForm/sections/index'

/** 从 CDN / 本地 URL 取末段文件名，供上传条展示 */
function fileNameFromUrl(url) {
  if (!url) return ''
  try {
    const path = String(url).split('?')[0]
    const parts = path.split('/')
    return parts[parts.length - 1] || url
  } catch (e) {
    return url
  }
}

export default {
  name: 'GenericResourceFormShell',
  components: {
    GenericResourceBasicFormHost,
    GenericResourceZipUpload,
    ZipPackageRequirement,
    BasicInfoIdleAnim,
    AiAssistantBasicInfoSummary,
    ...GENERIC_RESOURCE_GOVERNANCE_COMPONENTS,
  },
  props: {
    ruleForm: { type: Object, required: true },
    unlockData: { type: Object, required: true },
    isCreate: { type: Boolean, default: true },
    dataOptions: { type: Array, default: () => [] },
    governanceProfile: { type: String, default: 'link_default' },
    showCompletionStatus: { type: Boolean, default: false },
    /** 分类树 classifyTag :type；通用资源默认 21，Ai 角色为 17 */
    classifyType: { type: Number, default: 21 },
    /** 固定类型资源（如 ai_assistant）创建页跳过「待选类型」门禁 */
    skipTypeSelection: { type: Boolean, default: false },
  },
  data() {
    return {
      typeConfigValidationFailed: false,
      schemaValidationFailed: false,
      completionRevision: 0,
    }
  },
  watch: {
    'ruleForm.assetType'() {
      this.typeConfigValidationFailed = false
      this.schemaValidationFailed = false
      this.bumpCompletion()
    },
    'ruleForm.resourceName'() {
      this.bumpCompletion()
    },
    'ruleForm.jsonConfig.url'() {
      this.bumpCompletion()
    },
    'ruleForm.thumbUrl'() {
      this.bumpCompletion()
    },
    'unlockData.defaultLockGroupId'() {
      this.bumpCompletion()
    },
    'ruleForm.tagVOList': {
      deep: true,
      handler() {
        this.bumpCompletion()
      },
    },
  },
  computed: {
    hasSelectedType() {
      return !!String(this.ruleForm.assetType || '').trim()
    },
    isAwaitingType() {
      return this.isCreate && !this.skipTypeSelection && !this.hasSelectedType
    },
    isAiAssistantType() {
      return isAiAssistantType(this.ruleForm.assetType)
    },
    isWebCoverType() {
      return matchWebCoverType(this.ruleForm.assetType)
    },
    avatarPreviewUrl() {
      if (!this.isAiAssistantType) {
        return ''
      }
      const aiRole = this.ruleForm.jsonConfig && this.ruleForm.jsonConfig.aiRole
      return (aiRole && aiRole.avatar) || this.ruleForm.thumbUrl || ''
    },
    typeEntry() {
      return resolveGenericResourceTypeEntry(this.ruleForm.assetType)
    },
    layout() {
      return resolveFormLayout(this.ruleForm.assetType, this.governanceProfile)
    },
    layoutSections() {
      return this.layout.sections || []
    },
    isSchemaType() {
      return (
        this.typeEntry &&
        this.typeEntry.mode === GENERIC_RESOURCE_BASIC_FORM_MODE.SCHEMA
      )
    },
    showSchemaSection() {
      return this.isSchemaType && this.hasSelectedType
    },
    showTypeConfigSection() {
      return this.hasSelectedType && shouldShowTypeConfigSection(this.ruleForm.assetType)
    },
    typeConfigSectionTitle() {
      return getTypeConfigSectionTitle(this.ruleForm.assetType)
    },
    schemaSectionTitle() {
      return getSchemaSectionTitle(this.ruleForm.assetType)
    },
    zipPackageSpec() {
      return getZipPackageSpec(this.ruleForm.assetType)
    },
    showZipPackageRequirement() {
      return shouldShowZipPackageRequirement(this.ruleForm.assetType)
    },
    useZipBandLayout() {
      if (!this.showZipPackageRequirement) {
        return false
      }
      const type = String(this.ruleForm.assetType || '')
      // workout 全系与 action 一致：zip 要求放在名称下方 detailSlot，不横跨整行
      if (type.startsWith('workout_')) {
        return false
      }
      const exactCount = ((this.zipPackageSpec && this.zipPackageSpec.exact) || []).length
      return exactCount >= 4
    },
    showZipInDetailSlot() {
      return this.showZipPackageRequirement && !this.useZipBandLayout
    },
    idleAnimHint() {
      if (this.isAwaitingType) {
        return '先在顶栏选择资源类型，再填写名称与下方配置'
      }
      if (this.isAiAssistantType) {
        return '请先填写运营名称，再在下方「Ai 角色配置」填写角色字段'
      }
      if (this.isWebCoverType) {
        return '填好名称，左侧上传封面，下方填写网页链接'
      }
      if (isNonFileType(this.ruleForm.assetType)) {
        return '填好名称，在下方继续配置即可'
      }
      if (this.requiresFileUpload) {
        return '填好名称，左侧上传文件即可'
      }
      return '填好名称就可以继续啦'
    },
    requiresFileUpload() {
      return requiresFileUpload(this.ruleForm.assetType)
    },
    isZipOnlyType() {
      return isZipOnlyType(this.ruleForm.assetType)
    },
    showUploadDone() {
      return this.requiresFileUpload && !!this.ruleForm.jsonConfig.url
    },
    uploadZoneHint() {
      if (this.isAwaitingType) {
        return '请先在顶栏选择资源类型'
      }
      if (this.isWebCoverType) {
        return '点击上传封面图'
      }
      if (isNonFileType(this.ruleForm.assetType)) {
        return '该类型无需上传文件，请填写下方配置项'
      }
      if (this.isZipOnlyType) {
        return '点击上传 .zip 文件'
      }
      return '选择本地文件，上传后写入资源地址'
    },
    uploadUrlDisplay() {
      return fileNameFromUrl(this.ruleForm.jsonConfig && this.ruleForm.jsonConfig.url)
    },
    webCoverDisplayName() {
      return fileNameFromUrl(this.ruleForm.thumbUrl)
    },
    displayKey() {
      const key = this.ruleForm.key
      if (!key) return '—'
      if (key.length <= 36) return key
      return `${key.slice(0, 16)}…${key.slice(-12)}`
    },
    editPreviewUrl() {
      if (this.isAiAssistantType) {
        return this.avatarPreviewUrl
      }
      return this.ruleForm.thumbUrl || ''
    },
    previewSrcList() {
      return this.editPreviewUrl ? [this.editPreviewUrl] : []
    },
    useZipUploadComponent() {
      return this.requiresFileUpload && (this.isZipOnlyType || this.isCreate)
    },
    typeConfigSnapshot() {
      void this.completionRevision
      const ref = this.getActiveBasicFormRef()
      return ref && ref.captureSnapshot ? ref.captureSnapshot() : {}
    },
    completion() {
      void this.completionRevision
      return computeGenericResourceCompletion({
        ruleForm: this.ruleForm,
        unlockData: this.unlockData,
        hasSelectedType: this.hasSelectedType,
        typeConfigSnapshot: this.typeConfigSnapshot,
      })
    },
    typeConfigStatusLabel() {
      const status = this.completion.sectionStatus.typeConfig
      if (status === 'done') {
        return '✓ 已完成'
      }
      if (status === 'optional') {
        return '选填'
      }
      return '未完成'
    },
    typeConfigStatusClass() {
      const status = this.completion.sectionStatus.typeConfig
      if (status === 'done') {
        return 'linkFormSection__status--done'
      }
      if (status === 'optional') {
        return 'linkFormSection__status--optional'
      }
      return 'linkFormSection__status--pending'
    },
  },
  methods: {
    bumpCompletion() {
      this.completionRevision += 1
      this.$emit('completion-changed')
    },
    resolveGovernanceComponent(section) {
      const name = section && section.component
      if (!name) {
        return null
      }
      return GENERIC_RESOURCE_GOVERNANCE_COMPONENTS[name] || name
    },
    governanceProps(sectionId) {
      if (sectionId === 'unlock') {
        return {
          unlockData: this.unlockData,
          showCompletionStatus: this.showCompletionStatus,
          completionDone: this.completion.sectionStatus.unlock === 'done',
        }
      }
      if (sectionId === 'tag') {
        // workout_action：Tag 标部位，必填；其它类型仍可选关联，不进完成度
        const tagsRequired = String(this.ruleForm.assetType || '') === 'workout_action'
        return {
          ruleForm: this.ruleForm,
          tagsRequired,
          showCompletionStatus: this.showCompletionStatus && tagsRequired,
          completionDone: this.completion.sectionStatus.tag === 'done',
        }
      }
      if (
        sectionId === 'classify' ||
        sectionId === 'filter' ||
        sectionId === 'other'
      ) {
        return {
          ruleForm: this.ruleForm,
          dataOptions: this.dataOptions,
          resourceType: this.classifyType,
        }
      }
      return {}
    },
    getActiveBasicFormRef() {
      if (this.isSchemaType) {
        return this.$refs.schemaBasicForm
      }
      return this.$refs.basicForm
    },
    buildExtaPayload() {
      const ref = this.getActiveBasicFormRef()
      return ref && ref.buildExtaPayload ? ref.buildExtaPayload() : {}
    },
    loadFromExta(exta) {
      const apply = () => {
        const ref = this.getActiveBasicFormRef()
        if (ref && ref.loadFromExta) {
          ref.loadFromExta(exta)
          return true
        }
        return false
      }
      this.$nextTick(() => {
        if (!apply()) {
          // 类型配置子表单晚于 Shell 一拍挂载，再试一次避免编辑页 exta 漏灌
          this.$nextTick(() => {
            apply()
          })
        }
      })
    },
    resetBasicForm() {
      const ref = this.getActiveBasicFormRef()
      if (ref && ref.reset) {
        ref.reset()
      }
    },
    validateBasicForm() {
      // 类型配置 / schema 区本应挂载但 Host 缺失时不能静默通过，否则会写出空 exta
      const needsTypeConfigHost = this.showTypeConfigSection || this.showSchemaSection
      const ref = this.getActiveBasicFormRef()
      if (!ref || !ref.validate) {
        if (needsTypeConfigHost) {
          if (this.showSchemaSection) {
            this.schemaValidationFailed = true
            this.typeConfigValidationFailed = false
          } else {
            this.typeConfigValidationFailed = true
            this.schemaValidationFailed = false
          }
          return Promise.reject(new Error('训练资源配置未加载，请刷新后重试'))
        }
        this.typeConfigValidationFailed = false
        this.schemaValidationFailed = false
        return Promise.resolve()
      }
      return ref.validate().then((result) => {
        this.typeConfigValidationFailed = false
        this.schemaValidationFailed = false
        return result
      }).catch((err) => {
        if (this.showSchemaSection) {
          this.schemaValidationFailed = true
          this.typeConfigValidationFailed = false
        } else {
          this.typeConfigValidationFailed = true
          this.schemaValidationFailed = false
        }
        return Promise.reject(err)
      })
    },
    validateGovernance() {
      const promises = this.layoutSections.map((section) => {
        const ref = this.$refs['gov-' + section.id]
        const comp = Array.isArray(ref) ? ref[0] : ref
        if (comp && comp.validate) {
          return comp.validate()
        }
        return Promise.resolve()
      })
      return Promise.all(promises)
    },
    validateAll() {
      return this.validateBasicForm().then(() => this.validateGovernance())
    },
    scrollToTypeConfig() {
      const ref = this.showSchemaSection
        ? this.$refs.schemaConfigSection
        : this.$refs.typeConfigSection
      const el = ref && (ref.$el || ref)
      if (el && typeof el.scrollIntoView === 'function') {
        el.scrollIntoView({ behavior: 'smooth', block: 'start' })
      }
    },
    getCompletionSnapshot() {
      return this.completion
    },
    scrollToFirstError() {
      const pending = (this.completion.pendingItems || [])[0]
      if (pending && pending.sectionId === 'header') {
        return { sectionId: 'header' }
      }

      const root = this.$el
      if (!root) {
        return null
      }

      const fieldError = root.querySelector('.el-form-item.is-error')
      if (fieldError) {
        fieldError.scrollIntoView({ behavior: 'smooth', block: 'center' })
        return fieldError
      }

      if (pending && pending.sectionId) {
        const section = root.querySelector(`[data-section-id="${pending.sectionId}"]`)
        if (section) {
          section.scrollIntoView({ behavior: 'smooth', block: 'start' })
          return section
        }
      }

      const invalidSection = root.querySelector('.linkFormSection--invalid')
      if (invalidSection) {
        invalidSection.scrollIntoView({ behavior: 'smooth', block: 'start' })
        return invalidSection
      }

      return null
    },
    onZipUploaded(_url, fileName) {
      // 包名就是运营看到的名称。括号里的内部编号不进资源名，上传后再改名字仍然有效。
      if (this.ruleForm.assetType === 'stickers' && fileName) {
        this.$set(this.ruleForm, 'resourceName', stickerNameFromFile(fileName))
      }
      this.bumpCompletion()
      this.$emit('url-uploaded')
    },
    captureSnapshot() {
      const basic = this.getActiveBasicFormRef()
      const governance = {}
      this.layoutSections.forEach((section) => {
        const ref = this.$refs['gov-' + section.id]
        const comp = Array.isArray(ref) ? ref[0] : ref
        if (comp && comp.captureSnapshot) {
          governance[section.id] = comp.captureSnapshot()
        }
      })
      return {
        basicForm: basic && basic.captureSnapshot ? basic.captureSnapshot() : {},
        governance,
      }
    },
    uploadUrl() {
      if (this.isAwaitingType) {
        this.$message.info('请先在顶栏选择资源类型')
        return
      }
      if (!this.requiresFileUpload) {
        this.$message.info('该类型无需上传文件')
        return
      }
      const input = document.createElement('input')
      input.setAttribute('type', 'file')
      if (this.isZipOnlyType) {
        input.setAttribute('accept', '.zip,application/zip')
      }
      input.addEventListener('change', (event) => {
        const files = event.target.files
        if (files.length > 0) {
          const file = files[0]
          const formatCheck = validateLocalFileForAssetType(
            this.ruleForm.assetType,
            file.name,
            file.size
          )
          if (!formatCheck.ok) {
            this.$message.error(formatCheck.message)
            input.remove()
            return
          }
          const formData = new FormData()
          formData.append('file', file)
          uploadFile(formData)
            .then((res) => {
              const { data } = res
              const urlCheck = validateUploadedUrlForAssetType(
                this.ruleForm.assetType,
                data
              )
              if (!urlCheck.ok) {
                this.$message.error(urlCheck.message)
                return
              }
              this.ruleForm.jsonConfig.url = data
              this.$emit('url-uploaded', data)
            })
            .catch(() => {
              this.$message.error('文件上传失败，请检查网络后重试')
            })
          input.remove()
        }
      })
      input.click()
    },
    clearUpload() {
      this.ruleForm.jsonConfig.url = ''
      this.bumpCompletion()
      this.$emit('url-cleared')
    },
    /**
     * web 封面：只写 thumbUrl，不写 jsonConfig.url。
     * 为什么：url 会被查看页当成「可下载资源包」；封面应走列表/详情缩略图字段。
     */
    uploadWebCover() {
      if (this.isAwaitingType) {
        this.$message.info('请先在顶栏选择资源类型')
        return
      }
      if (!this.isWebCoverType) {
        return
      }
      const input = document.createElement('input')
      input.setAttribute('type', 'file')
      input.setAttribute('accept', 'image/jpeg,image/png,image/webp,image/gif,.jpg,.jpeg,.png,.webp,.gif')
      input.addEventListener('change', (event) => {
        const files = event.target.files
        if (files.length > 0) {
          const file = files[0]
          const formatCheck = validateLocalFileForAssetType(
            this.ruleForm.assetType,
            file.name
          )
          if (!formatCheck.ok) {
            this.$message.error(formatCheck.message)
            input.remove()
            return
          }
          const formData = new FormData()
          formData.append('file', file)
          uploadFile(formData)
            .then((res) => {
              const { data } = res
              const urlCheck = validateUploadedUrlForAssetType(
                this.ruleForm.assetType,
                data
              )
              if (!urlCheck.ok) {
                this.$message.error(urlCheck.message)
                return
              }
              this.ruleForm.thumbUrl = data
              this.bumpCompletion()
              this.$emit('cover-uploaded', data)
            })
            .catch(() => {
              this.$message.error('封面上传失败，请检查网络后重试')
            })
          input.remove()
        }
      })
      input.click()
    },
    clearWebCover() {
      this.ruleForm.thumbUrl = ''
      this.bumpCompletion()
      this.$emit('cover-cleared')
    },
    copyKey() {
      const key = this.ruleForm.key
      if (!key) return
      if (navigator.clipboard && navigator.clipboard.writeText) {
        navigator.clipboard.writeText(key).then(() => {
          this.$message.success('Key 已复制')
        })
        return
      }
      this.$message.warning('当前环境不支持自动复制')
    },
  },
}
</script>

<style scoped>
.linkFormGovernanceGate--locked {
  position: relative;
  opacity: 0.62;
  pointer-events: none;
  user-select: none;
}

.linkFormGovernanceGate--locked::after {
  content: '';
  position: absolute;
  inset: 0;
  z-index: 2;
  cursor: not-allowed;
}

.genericResourceFormShell >>> .linkFormSection--typeConfig.linkFormSection--invalid,
.genericResourceFormShell >>> .linkFormSection--schema.linkFormSection--invalid {
  box-shadow: inset 0 0 0 1px rgba(245, 108, 108, 0.55);
}

.genericResourceFormShell >>> .linkFormSection--typeConfig.linkFormSection--invalid .el-card__header,
.genericResourceFormShell >>> .linkFormSection--schema.linkFormSection--invalid .el-card__header {
  background: rgba(254, 242, 242, 0.45);
}

.genericResourceFormShell >>> .linkFormBasic__urlField {
  margin-bottom: 0;
}
.genericResourceFormShell >>> .linkFormBasic__urlField .el-form-item__content {
  line-height: 0;
}

.linkFormSection__header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
}

.linkFormSection__status {
  font-size: 12px;
  font-weight: 500;
}

.linkFormSection__status--pending {
  color: #d97706;
}

.linkFormSection__status--done {
  color: #16a34a;
}

.linkFormSection__status--optional {
  color: #6b7280;
}
</style>
